import React from 'react';
import { Icons, SVG, SVGIcon } from '../../../components/atoms/SVG';
import { SpanProps } from '../../../types/HtmlProps';
import { PageType } from '../../../types/api/PageType';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import bem from '../../../lib/bemUtils/bem';

export type PageTypeIconModifiers = 'large-rounded' | 'x-large-round' | 'align-self-top';

type PageTypeIconProps = {
  pageType: PageType;
  modifier?: Modifier<PageTypeIconModifiers>;
};
type colorType =
  | 'charcoal'
  | 'burgundy'
  | 'vale'
  | 'brown'
  | 'green'
  | 'mint'
  | 'lightmint'
  | 'aqua-lake'
  | 'darkblue'
  | 'fadedblue'
  | 'violet-ink'
  | 'safety-orange'
  | 'persian-red'
  | 'fuzzy-wuzzy'
  | 'midnight-green'
  | 'deep-marine'
  | 'golden-foil'
  | 'gold-taffeta'
  | 'amethyst';

type IconAndColor = { icon: SVGIcon; color: colorType };
const getIconAndColorType = (pageType: PageType): IconAndColor => {
  switch (pageType) {
    case PageType.teamroom:
      return { icon: Icons.TEAMROOM_PEOPLE, color: 'green' };
    case PageType.topichome:
    case PageType.topic:
      return { icon: Icons.TOPIC, color: 'charcoal' };
    case PageType.faqlist:
    case PageType.faq:
    case PageType.faqlistpost:
      return { icon: Icons.FAQ, color: 'brown' };
    case PageType.forum:
      return { icon: Icons.FORUM, color: 'persian-red' };
    case PageType.forumthread:
      return { icon: Icons.FORUM, color: 'fuzzy-wuzzy' };
    case PageType.orgunithome:
    case PageType.orgunit:
      return { icon: Icons.ORGANISATION, color: 'charcoal' };
    case PageType.poll:
    case PageType.survey:
      return { icon: Icons.SURVEY, color: 'safety-orange' };
    case PageType.event:
    case PageType.on2event:
      return { icon: Icons.CALENDAR, color: 'lightmint' };
    case PageType.eventlist:
    case PageType.on2eventlist:
      return { icon: Icons.CALENDAR, color: 'mint' };
    case PageType.processhome:
    case PageType.process:
      return { icon: Icons.PROCESS2, color: 'charcoal' };
    case PageType.locationhome:
    case PageType.location:
      return { icon: Icons.LOCATION, color: 'charcoal' };
    case PageType.wikihome:
      return { icon: Icons.WIKI, color: 'burgundy' };
    case PageType.wiki:
      return { icon: Icons.WIKI, color: 'vale' };
    case PageType.blog:
    case PageType.on2blog:
      return { icon: Icons.BLOG, color: 'burgundy' };
    case PageType.blogpost:
    case PageType.on2blogpost:
      return { icon: Icons.BLOG, color: 'vale' };
    case PageType.pagelist:
    case PageType.pagelistteaser:
      return { icon: Icons.PAGE_LIST, color: 'darkblue' };
    case PageType.document:
      return { icon: Icons.PAGE_LIST, color: 'midnight-green' };
    case PageType.room:
      return { icon: Icons.RESOURCES_LIST, color: 'aqua-lake' };
    case PageType.roomhome:
      return { icon: Icons.RESOURCES_LIST, color: 'deep-marine' };
    case PageType.sharedfileshome:
      return { icon: Icons.FILES_SHARED, color: 'violet-ink' };
    case PageType.sharedfilesdirectory:
      return { icon: Icons.FILES_SHARED, color: 'amethyst' };
    case PageType.task:
      return { icon: Icons.TASK, color: 'charcoal' };
    case PageType.externallink:
    case PageType.externalapplication:
      return { icon: Icons.EXTERNAL_LINK_ARROW, color: 'fadedblue' };
    case PageType.imagegallery:
      return { icon: Icons.IMAGE_GALLERY, color: 'darkblue' };
    case PageType.structurediagram:
      return { icon: Icons.GRAPHIC_STRUCTURE, color: 'darkblue' };
    case PageType.formapplication:
    case PageType.sonformapplication:
      return { icon: Icons.FORM, color: 'gold-taffeta' };
    case PageType.ticketbooking:
      return { icon: Icons.TICKET_BOOKING, color: 'golden-foil' };
    case PageType.node:
      return { icon: Icons.BLANK_PAGE, color: 'darkblue' };
    case PageType.nodehome:
      return { icon: Icons.BLANK_PAGE, color: 'charcoal' };
    default:
      return { icon: Icons.BLANK_PAGE, color: 'darkblue' };
  }
};

export function PageTypeIcon({ pageType, modifier, ...spanProps }: PageTypeIconProps & SpanProps) {
  const { icon, color } = getIconAndColorType(pageType);

  const baseClass = 'page-type-icon__container';
  const modifierClass = bem(baseClass, modifier);
  const colorClass = `${baseClass}--${color}`;
  const cssClass = [modifierClass, colorClass].filter(Boolean);

  return (
    <span className={cssClass.join(' ')} {...spanProps}>
      <SVG svgSpriteId={icon} svgClass="page-type-icon__icon" />
    </span>
  );
}
